import React, { useMemo, useRef, useEffect } from 'react';
import { compute3DMeshDataAndPosition } from '../../utils';
import { createReactThreeGeometry } from '../../reactThreeUtils';
import { useSpring, a } from 'react-spring/three';
import * as constantsViewer from '../../constants';
import data from './data/batten';

export function Batten({
  battenPosition = 'FL',
  cabinetWidth = 1000,
  cabinetDepth = 420,
  unselected = false,
}) {
  const mesh = useRef();

  const baseData = useMemo(() => compute3DMeshDataAndPosition(data.batten), [
    data.batten,
  ]);

  const [spring, set] = useSpring(() => ({
    position: getPosition(
      baseData.position,
      baseData.size,
      cabinetWidth,
      cabinetDepth,
      battenPosition,
    ),
    immediate: !constantsViewer.REACT_SPRING_ANIMATIONS_ACTIVE,
    config: constantsViewer.REACT_SPRING_ANIMATIONS_CONF,
  }));

  useEffect(() => {
    let self = mesh.current;
    if (self) {
      set({
        position: getPosition(
          baseData.position,
          baseData.size,
          cabinetWidth,
          cabinetDepth,
          battenPosition,
        ),
      });
    }
  }, [cabinetWidth, cabinetDepth]);

  return (
    <a.mesh
      ref={mesh}
      receiveShadow
      castShadow
      position={spring.position}
      rotation={getRotation(battenPosition)}
    >
      {createReactThreeGeometry(baseData)}
      <meshPhongMaterial
        attach="material"
        color={constantsViewer.MATERIAL_BATTEN_COLOR}
        specular={constantsViewer.MATERIAL_BATTEN_SPECULAR}
        shininess={constantsViewer.MATERIAL_BATTEN_SHININESS}
        opacity={unselected ? constantsViewer.OPACITY_UNSELECTED_SHELF : 1}
        transparent
      />
    </a.mesh>
  );
}

export function FeetBatten({
  battenPosition = 'FL',
  battenHeight = 140,
  cabinetHeight = 590,
  cabinetWidth = 1000,
  cabinetDepth = 420,
  unselected = false,
}) {
  const mesh = useRef();

  const baseData = useMemo(
    () => compute3DMeshDataAndPosition(data.feetBatten),
    [data.feetBatten],
  );

  const [spring, set] = useSpring(() => ({
    position: getPositionFeetBatten(
      baseData.position,
      baseData.size,
      battenHeight,
      cabinetHeight,
      cabinetWidth,
      cabinetDepth,
      battenPosition,
    ),
    scale: getScaleFeetBatten(battenHeight),
    immediate: !constantsViewer.REACT_SPRING_ANIMATIONS_ACTIVE,
    config: constantsViewer.REACT_SPRING_ANIMATIONS_CONF,
  }));

  useEffect(() => {
    let self = mesh.current;
    if (self) {
      set({
        position: getPositionFeetBatten(
          baseData.position,
          baseData.size,
          battenHeight,
          cabinetHeight,
          cabinetWidth,
          cabinetDepth,
          battenPosition,
        ),
        scale: getScaleFeetBatten(battenHeight),
      });
    }
  }, [cabinetWidth, cabinetDepth, cabinetHeight, battenHeight]);

  return (
    <a.mesh
      ref={mesh}
      receiveShadow
      castShadow
      position={spring.position}
      scale={spring.scale}
      rotation={getRotation(battenPosition)}
    >
      {createReactThreeGeometry(baseData)}
      <meshPhongMaterial
        attach="material"
        color={constantsViewer.MATERIAL_BATTEN_COLOR}
        specular={constantsViewer.MATERIAL_BATTEN_SPECULAR}
        shininess={constantsViewer.MATERIAL_BATTEN_SHININESS}
        opacity={unselected ? constantsViewer.OPACITY_UNSELECTED_SHELF : 1}
        transparent
      />
    </a.mesh>
  );
}

export function AdjustableFeetBatten({
  battenPosition = 'FL',
  battenMaterial = 'A',
  battenHeight = 140,
  cabinetHeight = 590,
  cabinetWidth = 1000,
  cabinetDepth = 420,
  unselected = false,
}) {
  const mesh = useRef();

  const baseData = useMemo(
    () => compute3DMeshDataAndPosition(data.adjustableBatten),
    [data.adjustableBatten],
  );

  const [spring, set] = useSpring(() => ({
    position: getPositionAdjustableFeetBatten(
      baseData.position,
      baseData.size,
      battenHeight,
      cabinetHeight,
      cabinetWidth,
      cabinetDepth,
      battenPosition,
    ),
    scale: getScaleFeetBatten(battenHeight),
    immediate: !constantsViewer.REACT_SPRING_ANIMATIONS_ACTIVE,
    config: constantsViewer.REACT_SPRING_ANIMATIONS_CONF,
  }));

  useEffect(() => {
    let self = mesh.current;
    if (self) {
      set({
        position: getPositionAdjustableFeetBatten(
          baseData.position,
          baseData.size,
          battenHeight,
          cabinetHeight,
          cabinetWidth,
          cabinetDepth,
          battenPosition,
        ),
        scale: getScaleFeetBatten(battenHeight),
      });
    }
  }, [cabinetWidth, cabinetDepth, cabinetHeight, battenHeight]);

  let colors = [
    constantsViewer.MATERIAL_BATTEN_ALU_COLOR,
    constantsViewer.MATERIAL_BATTEN_ALU_SPECULAR,
    constantsViewer.MATERIAL_BATTEN_ALU_SHININESS,
  ];
  if (battenMaterial === 'N')
    colors = [
      constantsViewer.MATERIAL_BATTEN_ALU_BLACK_COLOR,
      constantsViewer.MATERIAL_BATTEN_ALU_BLACK_SPECULAR,
      constantsViewer.MATERIAL_BATTEN_ALU_BLACK_SHININESS,
    ];
  else if (battenMaterial === 'B')
    colors = [
      constantsViewer.MATERIAL_BATTEN_ALU_WHITE_COLOR,
      constantsViewer.MATERIAL_BATTEN_ALU_WHITE_SPECULAR,
      constantsViewer.MATERIAL_BATTEN_ALU_WHITE_SHININESS,
    ];

  return (
    <a.mesh
      ref={mesh}
      receiveShadow
      castShadow
      position={spring.position}
      scale={spring.scale}
      rotation={getRotation(battenPosition)}
    >
      {createReactThreeGeometry(baseData)}
      <meshPhongMaterial
        attach="material"
        color={colors[0]}
        specular={colors[1]}
        shininess={colors[2]}
        opacity={unselected ? constantsViewer.OPACITY_UNSELECTED_SHELF : 1}
        transparent
      />
    </a.mesh>
  );
}

function getRotation(battenPosition) {
  switch (battenPosition) {
    case 'FL':
      return [0, 0, 0];

    case 'FR':
      return [0, Math.PI / 2, 0];

    case 'BL':
      return [0, -Math.PI / 2, 0];

    case 'BR':
      return [0, -Math.PI, 0];

    default:
      return null;
  }
}

function getPosition(
  basePosition,
  size,
  cabinetWidth,
  cabinetDepth,
  battenPosition,
) {
  const cabinetDiffWith = cabinetWidth - constantsViewer.MODEL_CABINET_WIDTH;
  const cabinetDiffDepth = cabinetDepth - constantsViewer.MODEL_CABINET_DEPTH;

  switch (battenPosition) {
    case 'FL':
      return [
        basePosition.x - cabinetDiffWith / 2,
        basePosition.y,
        basePosition.z + cabinetDiffDepth / 2,
      ];

    case 'FR':
      return [
        basePosition.x +
          constantsViewer.MODEL_CABINET_WIDTH +
          cabinetDiffWith / 2 -
          size.x -
          10,
        basePosition.y,
        basePosition.z + cabinetDiffDepth / 2,
      ];

    case 'BL':
      return [
        basePosition.x - cabinetDiffWith / 2,
        basePosition.y,
        basePosition.z + cabinetDiffDepth / 2 - cabinetDepth + size.z + 2,
      ];

    case 'BR':
      return [
        basePosition.x +
          constantsViewer.MODEL_CABINET_WIDTH +
          cabinetDiffWith / 2 -
          size.x -
          8,
        basePosition.y,
        basePosition.z + cabinetDiffDepth / 2 - cabinetDepth + size.z + 2,
      ];

    default:
      return null;
  }
}

function getPositionFeetBatten(
  basePosition,
  size,
  battenHeight,
  cabinetHeight,
  cabinetWidth,
  cabinetDepth,
  battenPosition,
) {
  const cabinetDiffWith = cabinetWidth - constantsViewer.MODEL_CABINET_WIDTH;
  const cabinetDiffDepth = cabinetDepth - constantsViewer.MODEL_CABINET_DEPTH;
  const cabinetDiffHeight =
    cabinetHeight - battenHeight - constantsViewer.MODEL_CABINET_HEIGHT;

  switch (battenPosition) {
    case 'FL':
      return [
        basePosition.x - cabinetDiffWith / 2,
        basePosition.y - cabinetDiffHeight / 2,
        basePosition.z + cabinetDiffDepth / 2,
      ];

    case 'FR':
      return [
        basePosition.x +
          constantsViewer.MODEL_CABINET_WIDTH +
          cabinetDiffWith / 2 -
          size.x -
          10,
        basePosition.y - cabinetDiffHeight / 2,
        basePosition.z + cabinetDiffDepth / 2,
      ];

    case 'BL':
      return [
        basePosition.x - cabinetDiffWith / 2,
        basePosition.y - cabinetDiffHeight / 2,
        basePosition.z + cabinetDiffDepth / 2 - cabinetDepth + size.z + 2,
      ];

    case 'BR':
      return [
        basePosition.x +
          constantsViewer.MODEL_CABINET_WIDTH +
          cabinetDiffWith / 2 -
          size.x -
          8,
        basePosition.y - cabinetDiffHeight / 2,
        basePosition.z + cabinetDiffDepth / 2 - cabinetDepth + size.z + 2,
      ];

    default:
      return null;
  }
}

function getPositionAdjustableFeetBatten(
  basePosition,
  size,
  battenHeight,
  cabinetHeight,
  cabinetWidth,
  cabinetDepth,
  battenPosition,
) {
  const cabinetDiffWith = cabinetWidth - constantsViewer.MODEL_CABINET_WIDTH;
  const cabinetDiffDepth = cabinetDepth - constantsViewer.MODEL_CABINET_DEPTH;
  const cabinetDiffHeight =
    cabinetHeight - battenHeight - constantsViewer.MODEL_CABINET_HEIGHT;

  switch (battenPosition) {
    case 'FL':
      return [
        basePosition.x - cabinetDiffWith / 2,
        basePosition.y - cabinetDiffHeight / 2 - 8,
        basePosition.z + cabinetDiffDepth / 2,
      ];

    case 'FR':
      return [
        basePosition.x +
          constantsViewer.MODEL_CABINET_WIDTH +
          cabinetDiffWith / 2 -
          size.x -
          10,
        basePosition.y - cabinetDiffHeight / 2 - 8,
        basePosition.z + cabinetDiffDepth / 2,
      ];

    case 'BL':
      return [
        basePosition.x - cabinetDiffWith / 2,
        basePosition.y - cabinetDiffHeight / 2 - 8,
        basePosition.z + cabinetDiffDepth / 2 - cabinetDepth + size.z + 2,
      ];

    case 'BR':
      return [
        basePosition.x +
          constantsViewer.MODEL_CABINET_WIDTH +
          cabinetDiffWith / 2 -
          size.x -
          8,
        basePosition.y - cabinetDiffHeight / 2 - 8,
        basePosition.z + cabinetDiffDepth / 2 - cabinetDepth + size.z + 2,
      ];

    default:
      return null;
  }
}

function getScaleFeetBatten(battenHeight) {
  return [1, battenHeight / constantsViewer.MODEL_BATTEN_HEIGHT, 1];
}
